import { type ComponentPropsWithRef } from "react";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./Table.styled";
import { type Layout } from "./Table.types";
import { Typography, type TypographyProps } from "../Typography";

export type TableProps = PropsWithOptionalTestID &
  Omit<ComponentPropsWithRef<"table">, "style" | "className"> & {
    layout?: Layout;
  };

function Table(props: TableProps) {
  const { layout, testID = "table", ...otherProps } = props;

  return (
    <S.Table
      width="100%"
      {...otherProps}
      $layout={layout}
      data-testid={testID}
    />
  );
}

type TableTheadProps = Omit<
  TypographyProps<"thead">,
  "component" | "variant" | "fontFamily"
>;

Table.Thead = function TableThead(props: TableTheadProps) {
  const { tokens } = useDesignTokens();
  const { table } = tokens;

  return (
    <Typography
      component="thead"
      variant={table.head.typography.variant}
      fontFamily={table.head.typography.fontFamily}
      {...props}
    />
  );
};

type TableTbodyProps = Omit<
  TypographyProps<"tbody">,
  "component" | "variant" | "fontFamily"
>;

Table.Tbody = function TableTbody(props: TableTbodyProps) {
  const { tokens } = useDesignTokens();
  const { table } = tokens;

  return (
    <Typography
      component="tbody"
      variant={table.body.typography.variant}
      fontFamily={table.body.typography.fontFamily}
      {...props}
    />
  );
};

type TableTrProps = ComponentPropsWithRef<"tr">;

Table.Tr = function TableTr(props: TableTrProps) {
  return <tr {...props} />;
};

type TableThProps = ComponentPropsWithRef<"th">;

Table.Th = function TableTh(props: TableThProps) {
  const { align = "right", ...otherProps } = props;

  return <th align={align} {...otherProps} />;
};

type TableTdProps = ComponentPropsWithRef<"td">;

Table.Td = function TableTd(props: TableTdProps) {
  const { align = "right", ...otherProps } = props;

  return <td align={align} {...otherProps} />;
};

export default Table;
